import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import pendingUrlService, { isValidSoraUrl } from '../services/pendingUrlService';
import analyticsService from '../services/analyticsService';
import appIcon from '../assets/app-icon.png';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const SORA_URL_REGEX = /^https?:\/\/(?:www\.)?sora\.chatgpt\.com\/p\/[a-z0-9_-]+/i;
const BEFORE_VIDEO_URL = 'https://pub-b6397c037d204f9da5330b78bf3a6ae0.r2.dev/19700121_1206_69611d22b12c81918483178f63c88aff.mp4';
const AFTER_VIDEO_URL = 'https://pub-b6397c037d204f9da5330b78bf3a6ae0.r2.dev/s_69611d22b12c81918483178f63c88aff%20(1).mp4';

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const BenefitCard = ({ icon, title, desc, gradient }) => (
  <div className="group relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-[1.02]">
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${gradient}`} />
    <div className="relative">
      <div className={`h-12 w-12 rounded-xl ${gradient} flex items-center justify-center mb-4 shadow-lg`}>
        <FontAwesomeIcon icon={icon} className="text-white text-lg" />
      </div>
      <h3 className="font-bold text-white text-lg mb-2">{title}</h3>
      <p className="text-slate-300 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

const StepCard = ({ number, title, desc, isLast }) => (
  <div className="relative flex items-start gap-4">
    {/* Connector line */}
    {!isLast && (
      <div className="absolute left-6 top-14 bottom-0 w-px bg-gradient-to-b from-indigo-500/50 to-transparent h-full" />
    )}
    
    <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold shadow-lg shadow-indigo-500/25">
      {number}
    </div>
    
    <div className="flex-1 pb-8">
      <h3 className="font-bold text-white text-lg mb-1">{title}</h3>
      <p className="text-slate-400 text-sm">{desc}</p>
    </div>
  </div>
);

const BeforeAfterCompare = ({ beforeSrc, afterSrc, caption, sliderLabel }) => {
  const [position, setPosition] = useState(50);
  const [isLoading, setIsLoading] = useState(true);
  const [beforeReady, setBeforeReady] = useState(false);
  const [afterReady, setAfterReady] = useState(false);
  const beforeVideoRef = React.useRef(null);
  const afterVideoRef = React.useRef(null);
  const syncIntervalRef = React.useRef(null);

  // Vérifier si les deux vidéos sont prêtes
  useEffect(() => {
    if (beforeReady && afterReady) {
      setIsLoading(false);
    }
  }, [beforeReady, afterReady]);

  // Démarrer la lecture synchronisée une fois les deux vidéos prêtes
  useEffect(() => {
    const beforeVideo = beforeVideoRef.current;
    const afterVideo = afterVideoRef.current;
    
    if (!beforeVideo || !afterVideo || isLoading) return;

    // Fonction de synchronisation robuste
    const syncVideos = () => {
      if (!beforeVideo || !afterVideo) return;
      
      const timeDiff = Math.abs(beforeVideo.currentTime - afterVideo.currentTime);
      
      // Si la différence est trop grande, forcer la synchronisation
      if (timeDiff > 0.05) {
        afterVideo.currentTime = beforeVideo.currentTime;
      }
    };

    // Démarrer les deux vidéos au même moment
    const startVideos = async () => {
      try {
        // Réinitialiser les deux vidéos au début
        beforeVideo.currentTime = 0;
        afterVideo.currentTime = 0;
        
        // Démarrer les deux vidéos simultanément
        await Promise.all([
          beforeVideo.play(),
          afterVideo.play()
        ]);
      } catch (error) {
        console.warn('Auto-play failed, waiting for user interaction:', error);
      }
    };

    // Synchronisation via timeupdate
    const handleTimeUpdate = () => {
      syncVideos();
    };

    // Synchronisation lorsque la vidéo "before" fait une boucle
    const handleSeeked = () => {
      if (afterVideo) {
        afterVideo.currentTime = beforeVideo.currentTime;
      }
    };

    // Démarrer un interval de synchronisation pour plus de robustesse
    syncIntervalRef.current = setInterval(syncVideos, 50);

    beforeVideo.addEventListener('timeupdate', handleTimeUpdate);
    beforeVideo.addEventListener('seeked', handleSeeked);
    beforeVideo.addEventListener('play', () => {
      afterVideo.play().catch(() => {});
    });
    beforeVideo.addEventListener('pause', () => {
      afterVideo.pause();
    });

    // Démarrer la lecture
    startVideos();

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      beforeVideo.removeEventListener('timeupdate', handleTimeUpdate);
      beforeVideo.removeEventListener('seeked', handleSeeked);
    };
  }, [isLoading]);

  // Gestionnaires pour le chargement des vidéos
  const handleBeforeCanPlayThrough = () => {
    setBeforeReady(true);
  };

  const handleAfterCanPlayThrough = () => {
    setAfterReady(true);
  };

  return (
    <div className="relative flex justify-center">
      {/* Container portrait 9:16 */}
      <div className="relative w-full max-w-[360px] aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 shadow-2xl">
        
        {/* Loader overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-sm">
            <div className="relative">
              {/* Spinner animé */}
              <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              <FontAwesomeIcon 
                icon="play" 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-400 text-lg" 
              />
            </div>
            <p className="mt-4 text-sm text-slate-400 animate-pulse">
              Chargement des vidéos...
            </p>
            {/* Barre de progression */}
            <div className="mt-3 w-32 h-1 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                style={{ width: `${(beforeReady ? 50 : 0) + (afterReady ? 50 : 0)}%` }}
              />
            </div>
          </div>
        )}

        {/* Video AFTER (arrière-plan complet) */}
        <video
          ref={afterVideoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={afterSrc}
          muted
          loop
          playsInline
          preload="auto"
          onCanPlayThrough={handleAfterCanPlayThrough}
        />
        
        {/* Badge AFTER - No watermark · 1440p (avec clip inversé) */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        >
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/90 backdrop-blur-sm text-xs text-white font-semibold shadow-lg">
            <FontAwesomeIcon icon="check" className="text-[10px]" />
            <span>No watermark · 1440p</span>
          </div>
        </div>

        {/* Container vidéo BEFORE avec clip */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        >
          <video
            ref={beforeVideoRef}
            className="absolute inset-0 h-full w-full object-cover"
            src={beforeSrc}
            muted
            loop
            playsInline
            preload="auto"
            onCanPlayThrough={handleBeforeCanPlayThrough}
          />
          
          {/* Badge BEFORE - Watermark · 720p */}
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/90 backdrop-blur-sm text-xs text-white font-semibold shadow-lg">
            <FontAwesomeIcon icon="xmark" className="text-[10px]" />
            <span>Watermark · 720p</span>
          </div>
        </div>

        {/* Barre de séparation verticale */}
        <div 
          className="absolute inset-y-0 z-20 pointer-events-none"
          style={{ left: `${position}%` }}
        >
          <div className="absolute inset-y-0 w-1 bg-white/90 -translate-x-1/2 shadow-lg" />
          <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-xl flex items-center justify-center border-2 border-white/50">
            <FontAwesomeIcon icon="arrows-left-right" className="text-indigo-600 text-sm" />
          </div>
        </div>

        {/* Slider invisible pour le contrôle */}
        <input
          type="range"
          min="0"
          max="100"
          value={position}
          onChange={(event) => setPosition(Number(event.target.value))}
          aria-label={sliderLabel}
          className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0 z-30"
          disabled={isLoading}
        />
      </div>
      
      {caption && (
        <p className="absolute -bottom-8 left-0 right-0 text-sm text-slate-300 text-center">{caption}</p>
      )}
    </div>
  );
};

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  const toggleLanguage = () => {
    const newLang = currentLang === 'fr' ? 'en' : 'fr';
    if (newLang === 'en') {
      navigate('/');
    } else {
      navigate(`/${newLang}`);
    }
    i18n.changeLanguage(newLang);
  };

  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm transition-all"
    >
      <FontAwesomeIcon icon="globe" className="text-xs" />
      <span className="uppercase font-medium">{currentLang}</span>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const LandingPage = () => {
  const { t, i18n } = useTranslation(['landing', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useParams();
  const { isAuthenticated } = useAuth();

  const [soraUrl, setSoraUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync language with URL param
  useEffect(() => {
    if (lang && (lang === 'fr' || lang === 'en')) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } else if (!lang && i18n.language !== 'en') {
      // Default to EN if no param
      // Optional: enforce EN on root
      // i18n.changeLanguage('en');
    }
  }, [lang, i18n]);

  // SEO Metadata
  const pageTitle = lang === 'fr' 
    ? 'Muzo Studio - Améliorez vos vidéos Sora (Upscale 4K)' 
    : 'Muzo Studio - AI Video Upscaling for Sora';
    
  const pageDescription = t('landing.hero.subtitle');
  // Canonical URL logic: EN is root, FR is /fr. If users visit /en, canonical points to root.
  const currentUrl = `https://muzo-studio.app${lang === 'fr' ? '/fr' : ''}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Muzo Studio",
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": pageDescription,
    "author": {
      "@type": "Organization",
      "name": "Muzo Studio"
    }
  };

  // Track page view on mount
  useEffect(() => {
    analyticsService.trackLandingPageView({
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      utm_campaign: searchParams.get('utm_campaign'),
    });
  }, [searchParams]);

  // If user is already authenticated and has a URL, redirect to upscale
  useEffect(() => {
    if (isAuthenticated && soraUrl && isValidSoraUrl(soraUrl)) {
      pendingUrlService.setPendingUrl(soraUrl);
      navigate('/upscale');
    }
  }, [isAuthenticated, soraUrl, navigate]);

  const validateUrl = useCallback((url) => {
    if (!url) {
      setUrlError('');
      return false;
    }
    if (!SORA_URL_REGEX.test(url)) {
      setUrlError(t('landing.hero.invalidUrl'));
      return false;
    }
    setUrlError('');
    return true;
  }, [t]);

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setSoraUrl(url);
    if (url) {
      validateUrl(url);
    } else {
      setUrlError('');
    }
  };

  const handlePaste = (e) => {
    // Track paste event
    analyticsService.track('landing_paste_clicked');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!soraUrl) {
      // If no URL, still allow to continue (will sign up first)
      analyticsService.trackLandingCtaClicked({ hasUrl: false });
      navigate('/signup?next=/upscale');
      return;
    }

    if (!validateUrl(soraUrl)) {
      return;
    }

    setIsSubmitting(true);
    
    // Store the URL
    pendingUrlService.setPendingUrl(soraUrl);
    
    // Track the event
    analyticsService.trackLandingCtaClicked({ hasUrl: true });
    analyticsService.trackLandingUrlEntered(soraUrl);

    // If already authenticated, go directly to upscale
    if (isAuthenticated) {
      navigate('/upscale');
    } else {
      // Otherwise, go to signup with redirect
      navigate('/signup?next=/upscale');
    }
  };

  const handleSecondaryCtaClick = () => {
    analyticsService.trackLandingCtaClicked({ location: 'secondary', hasUrl: !!soraUrl });
    if (soraUrl && isValidSoraUrl(soraUrl)) {
      pendingUrlService.setPendingUrl(soraUrl);
    }
    if (isAuthenticated) {
      navigate('/upscale');
    } else {
      navigate('/signup?next=/upscale');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-indigo-950 text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Hreflang tags - Critical for SEO */}
        <link rel="alternate" hreflang="en" href="https://muzo-studio.app" />
        <link rel="alternate" hreflang="fr" href="https://muzo-studio.app/fr" />
        <link rel="alternate" hreflang="x-default" href="https://muzo-studio.app" />
        
        {/* Open Graph updated dynamically */}
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        
        {/* JSON-LD for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        
        <html lang={lang || 'en'} />
      </Helmet>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={appIcon} alt="Muzo Studio" className="h-8 w-auto object-contain" />
              <span className="text-lg font-bold">
                Muzo<span className="text-indigo-400">Studio</span>
              </span>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors"
                >
                  {t('common:dashboard')}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                >
                  {t('landing.header.login')}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/30 rounded-full filter blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/30 rounded-full filter blur-[128px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/20 rounded-full filter blur-[96px]" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
            <FontAwesomeIcon icon="wand-magic-sparkles" className="text-indigo-400" />
            <span className="text-sm text-indigo-300 font-medium">Sora Video Enhancer</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="bg-gradient-to-r from-white via-white to-slate-300 bg-clip-text text-transparent">
              {t('landing.hero.title')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            {t('landing.hero.subtitle')}
          </p>

          {/* URL Input Form */}
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                    <FontAwesomeIcon 
                      icon="link" 
                      className={`text-lg ${soraUrl && isValidSoraUrl(soraUrl) ? 'text-emerald-400' : 'text-slate-400'}`} 
                    />
                  </div>
                  <input
                    type="url"
                    value={soraUrl}
                    onChange={handleUrlChange}
                    onPaste={handlePaste}
                    placeholder={t('landing.hero.placeholder')}
                    className={`w-full pl-12 pr-4 py-4 rounded-xl bg-slate-800/50 border ${
                      urlError 
                        ? 'border-red-500/50 focus:border-red-500' 
                        : soraUrl && isValidSoraUrl(soraUrl)
                        ? 'border-emerald-500/50 focus:border-emerald-500'
                        : 'border-white/10 focus:border-indigo-500'
                    } text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all text-lg`}
                  />
                  {soraUrl && isValidSoraUrl(soraUrl) && (
                    <div className="absolute inset-y-0 right-4 flex items-center">
                      <FontAwesomeIcon icon="check-circle" className="text-emerald-400" />
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-lg shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon="spinner" spin />
                  ) : (
                    <>
                      <span>{t('landing.hero.cta')}</span>
                      <FontAwesomeIcon icon="arrow-right" />
                    </>
                  )}
                </button>
              </div>
              
              {/* Error message */}
              {urlError && (
                <p className="absolute -bottom-6 left-0 text-sm text-red-400 flex items-center gap-1">
                  <FontAwesomeIcon icon="circle-exclamation" />
                  {urlError}
                </p>
              )}
            </div>

            {/* Microcopy */}
            <p className="mt-8 text-sm text-slate-400 flex items-center justify-center gap-2">
              <FontAwesomeIcon icon="cloud-arrow-up" className="text-slate-500" />
              {t('landing.hero.microcopy')}
            </p>
          </form>
        </div>
      </section>

      {/* Trust Badges - Hidden temporarily until we have real data
      <section className="py-12 px-4 border-y border-white/5 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            <TrustBadge value="10K+" label={t('landing.trust.users')} />
            <TrustBadge value="50K+" label={t('landing.trust.videos')} />
            <TrustBadge value="99%" label={t('landing.trust.satisfaction')} />
          </div>
        </div>
      </section>
      */}

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {t('landing.benefits.title')}
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <BenefitCard
              icon="bolt"
              title={t('landing.benefits.fast.title')}
              desc={t('landing.benefits.fast.desc')}
              gradient="bg-gradient-to-br from-amber-500 to-orange-600"
            />
            <BenefitCard
              icon="wand-magic-sparkles"
              title={t('landing.benefits.noWatermark.title')}
              desc={t('landing.benefits.noWatermark.desc')}
              gradient="bg-gradient-to-br from-indigo-500 to-purple-600"
            />
            <BenefitCard
              icon="shield-halved"
              title={t('landing.benefits.private.title')}
              desc={t('landing.benefits.private.desc')}
              gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
            />
            <BenefitCard
              icon="film"
              title={t('landing.benefits.quality.title')}
              desc={t('landing.benefits.quality.desc')}
              gradient="bg-gradient-to-br from-pink-500 to-rose-600"
            />
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            {t('landing.proof.title')}
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            {t('landing.proof.subtitle')}
          </p>
          
          <div className="max-w-4xl mx-auto pb-8">
            <BeforeAfterCompare
              beforeSrc={BEFORE_VIDEO_URL}
              afterSrc={AFTER_VIDEO_URL}
              caption={t('landing.proof.caption')}
              sliderLabel={t('landing.proof.sliderLabel')}
            />
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {t('landing.howItWorks.title')}
          </h2>
          
          <div className="space-y-2">
            <StepCard
              number="1"
              title={t('landing.howItWorks.step1.title')}
              desc={t('landing.howItWorks.step1.desc')}
            />
            <StepCard
              number="2"
              title={t('landing.howItWorks.step2.title')}
              desc={t('landing.howItWorks.step2.desc')}
            />
            <StepCard
              number="3"
              title={t('landing.howItWorks.step3.title')}
              desc={t('landing.howItWorks.step3.desc')}
              isLast
            />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-white/10 overflow-hidden">
            {/* Background effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-indigo-500/10 rounded-full filter blur-3xl" />
            </div>
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {t('landing.cta.title')}
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                {t('landing.cta.subtitle')}
              </p>
              
              <button
                onClick={handleSecondaryCtaClick}
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-lg shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all flex items-center justify-center gap-2 mx-auto"
              >
                <span>{t('landing.hero.ctaSecondary')}</span>
                <FontAwesomeIcon icon="arrow-right" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={appIcon} alt="Muzo Studio" className="h-6 w-auto object-contain" />
            <span className="text-sm text-slate-400">{t('landing.footer.copyright')}</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href="/legal/cgu" className="hover:text-white transition-colors">
              {t('landing.footer.terms')}
            </a>
            <a href="/legal/privacy" className="hover:text-white transition-colors">
              {t('landing.footer.privacy')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
